/**
 * Core Web Vitals instrumentation — S5-FE-05
 *
 * Reports LCP, INP, CLS to the backend via sendBeacon (non-blocking).
 * organizationId and role are read from the React Query cache when available.
 * Includes 1 retry with 2s delay if the auth cache is not yet hydrated.
 */
import type { Metric } from 'web-vitals';
import { queryClient } from './queryClient';

const ENDPOINT = '/api/metrics/web-vitals';
const RETRY_DELAY_MS = 2000;

interface CachedAuthUser {
  organizationId?: string;
  role?: string;
}

interface UserIdentity {
  organizationId: string | null;
  role: string | null;
}

/** Best-effort read of identity from the RQ cache (no React context needed). */
function getUserIdentity(): UserIdentity {
  try {
    const user = queryClient.getQueryData<CachedAuthUser>(['/api/auth/user']);
    return {
      organizationId: user?.organizationId ?? null,
      role: user?.role ?? null,
    };
  } catch {
    return { organizationId: null, role: null };
  }
}

/**
 * Reads identity with 1 retry (2s delay) if the auth query has not resolved yet.
 * Covers the race condition where web-vital events fire before /api/auth/user resolves.
 */
async function getUserIdentityWithRetry(): Promise<UserIdentity> {
  const initial = getUserIdentity();
  // role !== null indicates the query already resolved
  if (initial.role !== null) {
    return initial;
  }

  const queryState = queryClient.getQueryState(['/api/auth/user']);
  const isStillLoading = !queryState || queryState.status === 'pending';

  if (isStillLoading) {
    await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
    const retried = getUserIdentity();
    return {
      organizationId: retried.organizationId,
      role: retried.role ?? 'unknown',
    };
  }

  // Query resolved but without role (anomalous — should not happen)
  return { organizationId: initial.organizationId, role: 'unknown' };
}

function sendPayload(body: string): void {
  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: 'application/json' });
    navigator.sendBeacon(ENDPOINT, blob);
  } else {
    fetch(ENDPOINT, {
      method: 'POST',
      body,
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
    }).catch(() => {
      // Silently discard — never degrade UX for telemetry
    });
  }
}

async function sendMetric(metric: Metric): Promise<void> {
  const identity = await getUserIdentityWithRetry();

  const body = JSON.stringify({
    name: metric.name,
    value: metric.value,
    rating: metric.rating,
    path: window.location.pathname,
    organizationId: identity.organizationId,
    role: identity.role ?? 'unknown',
    timestamp: new Date().toISOString(),
  });

  sendPayload(body);
}

/** Lazily loads web-vitals and starts reporting CLS, INP, LCP. */
export function initWebVitals(): void {
  import('web-vitals').then(({ onCLS, onINP, onLCP }) => {
    onCLS((m) => { void sendMetric(m); });
    onINP((m) => { void sendMetric(m); });
    onLCP((m) => { void sendMetric(m); });
  }).catch(() => {
    // Non-critical — silently ignore if web-vitals fails to load
  });
}

// Exported for testing only
export { getUserIdentity, getUserIdentityWithRetry, RETRY_DELAY_MS };
